import { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../components/ui/table";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
    Check, X, Eye, FileText, AlertTriangle, User,
    DollarSign, ShoppingBag, Store, Users, Wallet,
    TrendingUp, Ban, CheckCircle, Package, Edit2, Truck
} from "lucide-react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";

const API_URL           = process.env.REACT_APP_BACKEND_URL + "/api";
const TRANSPORTEUR_URL  = (process.env.REACT_APP_TRANSPORTEUR_URL || "http://localhost:8004") + "/api";

export default function AdminDashboardPage() {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [stores, setStores] = useState([]);
    const [users, setUsers] = useState([]);
    const [withdrawals, setWithdrawals] = useState([]);
    const [loading, setLoading] = useState(true);

    // Dialog States
    const [selectedStore, setSelectedStore] = useState(null);
    const [rejectReason, setRejectReason] = useState("");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [actionType, setActionType] = useState(""); // 'approve' or 'reject'

    const [error, setError] = useState(null);

    // Supplier Products State
    const [supplierProducts, setSupplierProducts] = useState([]);
    const [loadingSupplierProducts, setLoadingSupplierProducts] = useState(false);
    const [editingMoq, setEditingMoq] = useState({}); // { [productId]: newMoqValue }
    const [savingMoq, setSavingMoq] = useState({});   // { [productId]: isLoading }

    // Livreurs State
    const [livreurs, setLivreurs] = useState([]);
    const [loadingLivreurs, setLoadingLivreurs] = useState(false);

    // Admin Monitoring States
    const [alerts, setAlerts] = useState([]);
    const [disputes, setDisputes] = useState([]);
    const [walletStats, setWalletStats] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [loadingFinancials, setLoadingFinancials] = useState(false);

    // Payables (frais livraison Sure More) state
    const [payables, setPayables] = useState([]);
    const [payablesLoading, setPayablesLoading] = useState(false);
    const [payableFilter, setPayableFilter] = useState('INVOICE_REQUESTED');
    const [payableDialog, setPayableDialog] = useState(null); // payable en cours de paiement
    const [paymentRef, setPaymentRef] = useState('');
    const [paymentNote, setPaymentNote] = useState('');
    const [payingId, setPayingId] = useState(null);

    const fetchPayables = async (status = payableFilter) => {
        setPayablesLoading(true);
        try {
            const res = await axios.get(`${TRANSPORTEUR_URL}/billing/admin`, {
                params: { status },
                headers: { Authorization: `Bearer ${localStorage.getItem('buymoreeasy_token')}` }
            });
            setPayables(res.data.payables || []);
        } catch (e) {
            console.error('fetchPayables', e);
        } finally {
            setPayablesLoading(false);
        }
    };

    const handlePayPayable = async (payableId) => {
        if (!paymentRef.trim()) { toast.error('Entrez la référence de paiement'); return; }
        setPayingId(payableId);
        try {
            await axios.post(`${TRANSPORTEUR_URL}/billing/admin/${payableId}/pay`,
                { payment_reference: paymentRef, note: paymentNote || undefined },
                { headers: { Authorization: `Bearer ${localStorage.getItem('buymoreeasy_token')}` } }
            );
            toast.success('Paiement confirmé !');
            setPayableDialog(null);
            setPaymentRef('');
            setPaymentNote('');
            fetchPayables();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors du paiement');
        } finally {
            setPayingId(null);
        }
    };

    // Dispute Actions state
    const [selectedDispute, setSelectedDispute] = useState(null);
    const [verdictType, setVerdictType] = useState("REFUND_CLIENT");
    const [verdictNote, setVerdictNote] = useState("");
    const [verdictPartialBuyer, setVerdictPartialBuyer] = useState("");
    const [verdictPartialSeller, setVerdictPartialSeller] = useState("");
    const [disputeDialogOpen, setDisputeDialogOpen] = useState(false);
    const [disputeResolveLoading, setDisputeResolveLoading] = useState(false);

    useEffect(() => {
        fetchData();
        fetchBackgroundData();
    }, []);

    const fetchLivreurs = async () => {
        setLoadingLivreurs(true);
        try {
            const res = await axios.get(`${API_URL}/transporteurs/admin/livreurs`);
            setLivreurs(res.data);
        } catch (e) {
            console.error("Erreur chargement livreurs", e);
        } finally {
            setLoadingLivreurs(false);
        }
    };

    const handleActivateLivreur = async (userId) => {
        try {
            await axios.post(`${API_URL}/transporteurs/admin/livreurs/${userId}/activate`);
            toast.success("Livreur activé avec succès");
            fetchLivreurs();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur lors de l'activation");
        }
    };

    const handleSuspendLivreur = async (userId) => {
        if (!window.confirm("Suspendre ce livreur ?")) return;
        try {
            await axios.post(`${API_URL}/transporteurs/admin/livreurs/${userId}/suspend`);
            toast.success("Livreur suspendu");
            fetchLivreurs();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        }
    };

    const handleRejectLivreur = async (userId, name) => {
        if (!window.confirm(`Rejeter et supprimer le compte de ${name} ?`)) return;
        try {
            await axios.delete(`${API_URL}/transporteurs/admin/livreurs/${userId}`);
            toast.success("Compte rejeté et supprimé");
            fetchLivreurs();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        }
    };

    const fetchData = async () => {
        try {
            const [statsRes, storesRes, usersRes, withdrawalsRes] = await Promise.all([
                axios.get(`${API_URL}/admin/stats`),
                axios.get(`${API_URL}/admin/stores`),
                axios.get(`${API_URL}/admin/users`),
                axios.get(`${API_URL}/admin/withdrawals`).catch(() => ({ data: [] }))
            ]);

            setStats(statsRes.data);
            setStores(storesRes.data);
            setUsers(usersRes.data);
            setWithdrawals(withdrawalsRes.data);
            setError(null);
        } catch (error) {
            console.error(error);
            if (error.response?.status === 403) {
                setError("Accès refusé. Vous devez être connecté en tant qu'administrateur.");
            } else {
                setError("Erreur serveur lors du chargement.");
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchBackgroundData = async () => {
        try {
            const [alertsRes, disputesRes] = await Promise.all([
                axios.get(`${API_URL}/admin/alerts`),
                axios.get(`${API_URL}/admin/disputes`)
            ]);
            setAlerts(alertsRes.data);
            setDisputes(disputesRes.data);
        } catch (e) {
            console.error("Error fetching alerts/disputes", e);
        }
    };

    const fetchFinancials = async () => {
        setLoadingFinancials(true);
        try {
            const [walletsRes, transRes] = await Promise.all([
                axios.get(`${API_URL}/admin/wallets`),
                axios.get(`${API_URL}/admin/wallet-transactions`)
            ]);
            setWalletStats(walletsRes.data);
            setTransactions(transRes.data);
        } catch (e) {
            toast.error("Erreur lors du chargement des données financières");
        } finally {
            setLoadingFinancials(false);
        }
    };

    const handleResolveAlert = async (alertId) => {
        try {
            await axios.post(`${API_URL}/admin/alerts/${alertId}/resolve`);
            toast.success("Alerte marquée comme résolue");
            fetchBackgroundData();
        } catch (e) {
            toast.error("Erreur lors de la résolution");
        }
    };

    const handleResolveDispute = async () => {
        if (!selectedDispute) return;
        setDisputeResolveLoading(true);
        try {
            const payload = {
                verdict: verdictType,
                notes: verdictNote || null,
            };
            if (verdictType === 'PARTIAL') {
                payload.partial_amount_buyer  = parseFloat(verdictPartialBuyer)  || 0;
                payload.partial_amount_seller = parseFloat(verdictPartialSeller) || 0;
            }
            // Nouveau endpoint Phase 3 : /orders/{order_id}/dispute/verdict
            await axios.post(`${API_URL}/orders/${selectedDispute.order_id}/dispute/verdict`, payload);
            toast.success("Verdict rendu avec succès — acheteur et vendeur notifiés");
            setDisputeDialogOpen(false);
            fetchBackgroundData();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur lors de l'arbitrage");
        } finally {
            setDisputeResolveLoading(false);
        }
    };

    const handleStoreAction = async () => {
        if (!selectedStore) return;

        try {
            if (actionType === 'approve') {
                await axios.post(`${API_URL}/admin/stores/${selectedStore.id}/approve`);
                toast.success("Boutique approuvée !");
            } else {
                await axios.post(`${API_URL}/admin/stores/${selectedStore.id}/reject?reason=${encodeURIComponent(rejectReason)}`);
                toast.success("Boutique rejetée.");
            }
            setDialogOpen(false);
            fetchData();
        } catch (e) {
            toast.error("Erreur lors de l'action");
        }
    };

    const handleWithdrawal = async (wid, action) => {
        const label = action === 'approve' ? 'valider' : 'rejeter';
        if (!window.confirm(`Voulez-vous ${label} ce retrait ?`)) return;
        try {
            await axios.post(`${API_URL}/admin/withdrawals/${wid}/process`, { action });
            toast.success(`Retrait ${action === 'approve' ? 'validé' : 'rejeté'}`);
            fetchData();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur traitement retrait");
        }
    };

    const toggleUserStatus = async (userId, currentStatus) => {
        try {
            await axios.put(`${API_URL}/admin/users/${userId}/status?active=${!currentStatus}`);
            toast.success("Statut utilisateur mis à jour");
            fetchData();
        } catch (e) {
            toast.error("Erreur mise à jour");
        }
    };

    const openDialog = (store, type) => {
        setSelectedStore(store);
        setActionType(type);
        setRejectReason("");
        setDialogOpen(true);
    };

    const handleStoreStatus = async (store, newStatus) => {
        const label = newStatus === 'SUSPENDED' ? 'suspendre' : 'réactiver';
        if (!window.confirm(`Voulez-vous ${label} la boutique "${store.name}" ?`)) return;
        try {
            await axios.patch(`${API_URL}/stores/${store.id}/status`, { status: newStatus });
            toast.success(`Boutique ${newStatus === 'SUSPENDED' ? 'suspendue' : 'réactivée'}`);
            fetchData();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur lors de la mise à jour du statut");
        }
    };

    const openDocument = (url) => {
        if (!url) return;
        if (url.startsWith('data:')) {
            // base64 data URI — convert to blob to allow opening in new tab
            const [header, b64] = url.split(',');
            const mime = header.match(/:(.*?);/)[1];
            const binary = atob(b64);
            const arr = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) arr[i] = binary.charCodeAt(i);
            const blob = new Blob([arr], { type: mime });
            const blobUrl = URL.createObjectURL(blob);
            window.open(blobUrl, '_blank');
        } else {
            const fullUrl = url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8002'}${url}`;
            window.open(fullUrl, '_blank');
        }
    };

    const fetchSupplierProducts = async () => {
        setLoadingSupplierProducts(true);
        try {
            const res = await axios.get(`${API_URL}/admin/supplier-products`);
            setSupplierProducts(res.data);
            // Initialize editing state with current MOQ values
            const moqInit = {};
            res.data.forEach(p => { moqInit[p.id] = p.min_wholesale_quantity ?? ""; });
            setEditingMoq(moqInit);
        } catch (e) {
            toast.error("Impossible de charger les produits fournisseurs");
        } finally {
            setLoadingSupplierProducts(false);
        }
    };

    const handleSaveMoq = async (productId) => {
        const newMoq = parseInt(editingMoq[productId]);
        if (!newMoq || newMoq < 1) {
            toast.error("Veuillez entrer une quantité valide (>= 1)");
            return;
        }
        setSavingMoq(prev => ({ ...prev, [productId]: true }));
        try {
            await axios.put(`${API_URL}/admin/products/${productId}/moq?moq=${newMoq}`);
            toast.success("MOQ mis à jour avec succès !");
            setSupplierProducts(prev => prev.map(p =>
                p.id === productId ? { ...p, min_wholesale_quantity: newMoq } : p
            ));
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur lors de la mise à jour");
        } finally {
            setSavingMoq(prev => ({ ...prev, [productId]: false }));
        }
    };

    if (loading) return <div className="p-8 flex justify-center">Chargement admin...</div>;

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col">
                <Navbar />
                <div className="flex-1 flex flex-col items-center justify-center p-4">
                    <div className="bg-white p-8 rounded-lg shadow-md text-center max-w-md">
                        <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                        <h1 className="text-xl font-bold mb-2">Accès Restreint</h1>
                        <p className="text-gray-600 mb-6">{error}</p>
                        <Button onClick={() => window.location.href = "/auth"}>Se connecter comme Admin</Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <Navbar />
            <div className="container mx-auto px-4 py-8 max-w-7xl">
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Administration</h1>
                        <p className="text-gray-500">Vue d'ensemble de la plateforme</p>
                    </div>
                    <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full border shadow-sm">
                        <User className="h-4 w-4 text-gray-400" />
                        <span className="text-sm font-medium">{user?.email}</span>
                        <Badge className="bg-purple-600">Admin</Badge>
                    </div>
                </div>

                {/* Stats Overview */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <StatCard title="Utilisateurs" value={stats?.total_users} icon={Users} color="bg-blue-500" />
                    <StatCard title="Boutiques" value={stats?.total_stores} icon={Store} color="bg-orange-500" />
                    <StatCard title="Commandes" value={stats?.total_orders} icon={ShoppingBag} color="bg-green-500" />
                    <StatCard title="Volume Total" value={`${(stats?.total_volume || 0).toLocaleString()} HTG`} icon={TrendingUp} color="bg-purple-500" />
                </div>

                <Tabs defaultValue="stores" className="space-y-6" onValueChange={(val) => {
                    if (val === 'suppliers') fetchSupplierProducts();
                    if (val === 'financials') fetchFinancials();
                    if (val === 'alerts' || val === 'disputes') fetchBackgroundData();
                }}>
                    <TabsList className="bg-white p-1 border flex-wrap h-auto">
                        <TabsTrigger value="stores" className="gap-2"><Store className="h-4 w-4" /> Boutiques</TabsTrigger>
                        <TabsTrigger value="users" className="gap-2"><Users className="h-4 w-4" /> Utilisateurs</TabsTrigger>
                        <TabsTrigger value="livreurs" className="gap-2" onClick={fetchLivreurs}>
                            <Truck className="h-4 w-4" /> Livreurs
                            {livreurs.filter(l => l.status === 'pending').length > 0 && (
                                <Badge className="ml-1 bg-orange-500 text-white text-xs px-1.5">
                                    {livreurs.filter(l => l.status === 'pending').length}
                                </Badge>
                            )}
                        </TabsTrigger>
                        <TabsTrigger value="alerts" className="gap-2">
                            <AlertTriangle className="h-4 w-4" /> Alertes
                            {alerts.filter(a => !a.is_resolved).length > 0 && <Badge className="ml-2 bg-red-500">{alerts.filter(a => !a.is_resolved).length}</Badge>}
                        </TabsTrigger>
                        <TabsTrigger value="disputes" className="gap-2">
                            <Ban className="h-4 w-4" /> Litiges
                            {disputes.filter(d => d.status === 'OPEN').length > 0 && <Badge className="ml-2 bg-red-500">{disputes.filter(d => d.status === 'OPEN').length}</Badge>}
                        </TabsTrigger>
                        <TabsTrigger value="financials" className="gap-2"><DollarSign className="h-4 w-4" /> Finances</TabsTrigger>
                        <TabsTrigger value="withdrawals" className="gap-2">
                            <Wallet className="h-4 w-4" /> Retraits
                            {stats?.pending_withdrawals > 0 && <Badge className="ml-2 bg-red-500">{stats?.pending_withdrawals}</Badge>}
                        </TabsTrigger>
                        <TabsTrigger value="suppliers" className="gap-2">
                            <Package className="h-4 w-4" /> Fournisseurs / MOQ
                        </TabsTrigger>
                        <TabsTrigger value="payables" className="gap-2"
                          onClick={() => fetchPayables('INVOICE_REQUESTED')}>
                            <DollarSign className="h-4 w-4" />
                            Livraisons Sure More
                            {payables.filter(p => p.status === 'INVOICE_REQUESTED').length > 0 && (
                                <Badge className="ml-1 bg-orange-500 text-white text-xs px-1.5">
                                    {payables.filter(p => p.status === 'INVOICE_REQUESTED').length}
                                </Badge>
                            )}
                        </TabsTrigger>
                    </TabsList>

                    {/* STORES TAB */}
                    <TabsContent value="stores">
                        <Card>
                            <CardHeader><CardTitle>Gestion des Boutiques</CardTitle></CardHeader>
                            <CardContent className="p-0">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Nom</TableHead>
                                            <TableHead>Type</TableHead>
                                            <TableHead>Statut</TableHead>
                                            <TableHead>Paiement</TableHead>
                                            <TableHead>Documents</TableHead>
                                            <TableHead className="text-right">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {stores.map(store => (
                                            <TableRow key={store.id}>
                                                <TableCell>
                                                    <div className="font-medium">{store.name}</div>
                                                    <div className="text-xs text-gray-500">{store.city}</div>
                                                </TableCell>
                                                <TableCell><Badge variant="outline">{store.store_type}</Badge></TableCell>
                                                <TableCell>
                                                    <Badge className={
                                                        store.status === 'ACTIVE' ? 'bg-green-500' :
                                                            store.status === 'REJECTED' ? 'bg-red-500' : 'bg-yellow-500'
                                                    }>{store.status}</Badge>
                                                </TableCell>
                                                <TableCell>
                                                    {store.is_paid ? <Badge variant="outline" className="text-green-600 border-green-200">Payé</Badge> : <Badge variant="outline" className="text-red-600 border-red-200">Non payé</Badge>}
                                                </TableCell>
                                                <TableCell>
                                                    {store.legal_docs_url ? (
                                                        store.legal_docs_url.startsWith('data:') ? (
                                                            <button
                                                                onClick={() => openDocument(store.legal_docs_url)}
                                                                className="text-blue-600 hover:underline flex items-center gap-1 text-sm"
                                                            >
                                                                <FileText className="h-4 w-4" /> Voir
                                                            </button>
                                                        ) : (
                                                            <a
                                                                href={store.legal_docs_url.startsWith('http') ? store.legal_docs_url : `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8002'}${store.legal_docs_url}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-blue-600 hover:underline flex items-center gap-1 text-sm"
                                                            >
                                                                <FileText className="h-4 w-4" /> Voir
                                                            </a>
                                                        )
                                                    ) : "-"}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex justify-end gap-2">
                                                        {store.status === 'PENDING_APPROVAL' && (
                                                            <>
                                                                <Button size="sm" className="bg-green-600 hover:bg-green-700 h-8 w-8 p-0" title="Approuver" onClick={() => openDialog(store, 'approve')}>
                                                                    <Check className="h-4 w-4" />
                                                                </Button>
                                                                <Button size="sm" variant="destructive" className="h-8 w-8 p-0" title="Rejeter" onClick={() => openDialog(store, 'reject')}>
                                                                    <X className="h-4 w-4" />
                                                                </Button>
                                                            </>
                                                        )}
                                                        {store.status === 'ACTIVE' && (
                                                            <Button size="sm" variant="outline" className="h-8 px-3 text-orange-600 border-orange-300 hover:bg-orange-50" onClick={() => handleStoreStatus(store, 'SUSPENDED')}>
                                                                <Ban className="h-3 w-3 mr-1" /> Suspendre
                                                            </Button>
                                                        )}
                                                        {store.status === 'SUSPENDED' && (
                                                            <Button size="sm" variant="outline" className="h-8 px-3 text-green-600 border-green-300 hover:bg-green-50" onClick={() => handleStoreStatus(store, 'ACTIVE')}>
                                                                <CheckCircle className="h-3 w-3 mr-1" /> Réactiver
                                                            </Button>
                                                        )}
                                                        {store.status === 'REJECTED' && (
                                                            <Button size="sm" variant="outline" className="h-8 px-3 text-blue-600 border-blue-300 hover:bg-blue-50" onClick={() => openDialog(store, 'approve')}>
                                                                <Check className="h-3 w-3 mr-1" /> Ré-approuver
                                                            </Button>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* USERS TAB */}
                    <TabsContent value="users">
                        <Card>
                            <CardHeader><CardTitle>Gestion des Utilisateurs</CardTitle></CardHeader>
                            <CardContent className="p-0">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Utilisateur</TableHead>
                                            <TableHead>Email</TableHead>
                                            <TableHead>Rôles</TableHead>
                                            <TableHead>Portefeuille</TableHead>
                                            <TableHead>Statut</TableHead>
                                            <TableHead className="text-right">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {users.map(u => (
                                            <TableRow key={u.id}>
                                                <TableCell className="font-medium">{u.first_name} {u.last_name}</TableCell>
                                                <TableCell>{u.email}</TableCell>
                                                <TableCell>
                                                    <div className="flex gap-1 flex-wrap">
                                                        {u.roles?.map(r => <Badge key={r.role_type} variant="secondary" className="text-xs">{r.role_type}</Badge>)}
                                                    </div>
                                                </TableCell>
                                                <TableCell>{u.wallet_balance?.toFixed(2)} HTG</TableCell>
                                                <TableCell>
                                                    <Badge className={u.is_active ? 'bg-green-500' : 'bg-red-500'}>
                                                        {u.is_active ? 'Actif' : 'Bloqué'}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <Button
                                                        size="sm"
                                                        variant={u.is_active ? "destructive" : "default"}
                                                        onClick={() => toggleUserStatus(u.id, u.is_active)}
                                                    >
                                                        {u.is_active ? <Ban className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* LIVREURS TAB */}
                    <TabsContent value="livreurs">
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center justify-between">
                                    <span className="flex items-center gap-2">
                                        <Truck className="h-5 w-5" /> Gestion des Livreurs
                                    </span>
                                    <div className="flex gap-2 text-sm font-normal">
                                        <span className="bg-orange-100 text-orange-700 px-2.5 py-0.5 rounded-full text-xs font-medium">
                                            {livreurs.filter(l => l.status === 'pending').length} en attente
                                        </span>
                                        <span className="bg-green-100 text-green-700 px-2.5 py-0.5 rounded-full text-xs font-medium">
                                            {livreurs.filter(l => l.status === 'active').length} actifs
                                        </span>
                                    </div>
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-0">
                                {loadingLivreurs ? (
                                    <div className="text-center py-10 text-gray-400">Chargement...</div>
                                ) : livreurs.length === 0 ? (
                                    <div className="text-center py-12 text-gray-400">
                                        <Truck className="h-10 w-10 mx-auto mb-3 opacity-20" />
                                        <p>Aucun livreur inscrit pour le moment.</p>
                                    </div>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Livreur</TableHead>
                                                <TableHead>Contact</TableHead>
                                                <TableHead>Entreprise</TableHead>
                                                <TableHead>Zone</TableHead>
                                                <TableHead>Inscription</TableHead>
                                                <TableHead>Statut</TableHead>
                                                <TableHead className="text-right">Actions</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {livreurs.map(l => (
                                                <TableRow key={l.id}>
                                                    <TableCell className="font-medium">
                                                        {l.first_name} {l.last_name}
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="text-sm">{l.email}</div>
                                                        {l.phone && <div className="text-xs text-gray-400">{l.phone}</div>}
                                                    </TableCell>
                                                    <TableCell className="text-sm text-gray-600">
                                                        {l.company_name || <span className="text-gray-300">—</span>}
                                                    </TableCell>
                                                    <TableCell className="text-sm">{l.coverage_zone || '—'}</TableCell>
                                                    <TableCell className="text-xs text-gray-400">
                                                        {l.created_at ? new Date(l.created_at).toLocaleDateString('fr-FR') : '—'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {l.status === 'pending' && (
                                                            <Badge className="bg-orange-100 text-orange-700 border-orange-200">En attente</Badge>
                                                        )}
                                                        {l.status === 'active' && (
                                                            <Badge className="bg-green-100 text-green-700 border-green-200">Actif</Badge>
                                                        )}
                                                        {l.status === 'suspended' && (
                                                            <Badge className="bg-red-100 text-red-700 border-red-200">Suspendu</Badge>
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        <div className="flex gap-2 justify-end">
                                                            {l.status === 'pending' && (
                                                                <>
                                                                    <Button
                                                                        size="sm"
                                                                        className="bg-green-600 hover:bg-green-700 text-white gap-1"
                                                                        onClick={() => handleActivateLivreur(l.id)}
                                                                    >
                                                                        <Check className="h-3.5 w-3.5" /> Activer
                                                                    </Button>
                                                                    <Button
                                                                        size="sm"
                                                                        variant="destructive"
                                                                        className="gap-1"
                                                                        onClick={() => handleRejectLivreur(l.id, `${l.first_name} ${l.last_name}`)}
                                                                    >
                                                                        <X className="h-3.5 w-3.5" /> Rejeter
                                                                    </Button>
                                                                </>
                                                            )}
                                                            {l.status === 'active' && (
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    className="text-orange-600 border-orange-300 hover:bg-orange-50 gap-1"
                                                                    onClick={() => handleSuspendLivreur(l.id)}
                                                                >
                                                                    <Ban className="h-3.5 w-3.5" /> Suspendre
                                                                </Button>
                                                            )}
                                                            {l.status === 'suspended' && (
                                                                <Button
                                                                    size="sm"
                                                                    className="bg-green-600 hover:bg-green-700 text-white gap-1"
                                                                    onClick={() => handleActivateLivreur(l.id)}
                                                                >
                                                                    <CheckCircle className="h-3.5 w-3.5" /> Réactiver
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* WITHDRAWALS TAB */}
                    <TabsContent value="withdrawals">
                        <Card>
                            <CardHeader><CardTitle>Demandes de Retrait</CardTitle></CardHeader>
                            <CardContent className="p-0">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Utilisateur</TableHead>
                                            <TableHead>Montant</TableHead>
                                            <TableHead>Méthode</TableHead>
                                            <TableHead>Compte</TableHead>
                                            <TableHead>Date</TableHead>
                                            <TableHead>Statut</TableHead>
                                            <TableHead className="text-right">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {withdrawals.map(w => (
                                            <TableRow key={w.id}>
                                                <TableCell>
                                                    <div className="font-medium">{w.user_name}</div>
                                                    <div className="text-xs text-gray-500">{w.user_email}</div>
                                                </TableCell>
                                                <TableCell className="font-bold text-lg">{w.amount.toFixed(2)} HTG</TableCell>
                                                <TableCell className="capitalize">{w.method}</TableCell>
                                                <TableCell className="text-xs text-gray-600">
                                                    <div className="font-mono bg-gray-100 px-2 py-1 rounded">{w.details?.account_number || w.details?.iban || w.details?.beneficiary || '—'}</div>
                                                    {w.details?.fee_htg > 0 && <div className="text-amber-600 mt-0.5">Frais : {w.details.fee_htg} HTG</div>}
                                                </TableCell>
                                                <TableCell>{new Date(w.created_at).toLocaleDateString('fr-FR')}</TableCell>
                                                <TableCell>
                                                    <Badge className={
                                                        w.status === 'COMPLETED' ? 'bg-green-500' :
                                                        w.status === 'REJECTED'  ? 'bg-red-500'   : 'bg-yellow-500'
                                                    }>{w.status === 'COMPLETED' ? 'Validé' : w.status === 'REJECTED' ? 'Rejeté' : 'En attente'}</Badge>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {w.status === 'PENDING' && (
                                                        <div className="flex justify-end gap-2">
                                                            <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleWithdrawal(w.id, 'approve')}>
                                                                Valider
                                                            </Button>
                                                            <Button size="sm" variant="destructive" onClick={() => handleWithdrawal(w.id, 'reject')}>
                                                                Rejeter
                                                            </Button>
                                                        </div>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {withdrawals.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={7} className="text-center py-8 text-gray-500">Aucune demande de retrait.</TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* SUPPLIERS MOQ TAB */}
                    <TabsContent value="suppliers">
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Package className="h-5 w-5 text-blue-600" />
                                    Gestion des Quantités Minimales — Produits Fournisseur
                                </CardTitle>
                                <p className="text-sm text-gray-500">Modifiez la quantité minimale de commande (MOQ) pour chaque produit d'un Fournisseur, selon leur demande.</p>
                            </CardHeader>
                            <CardContent className="p-0">
                                {loadingSupplierProducts ? (
                                    <div className="text-center py-12 text-gray-500">Chargement...</div>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead className="w-12">Photo</TableHead>
                                                <TableHead>Produit</TableHead>
                                                <TableHead>Boutique</TableHead>
                                                <TableHead className="text-right">Prix (HTG)</TableHead>
                                                <TableHead className="text-right">Stock</TableHead>
                                                <TableHead className="text-center w-48">MOQ Actuel</TableHead>
                                                <TableHead className="text-center w-56">Nouveau MOQ</TableHead>
                                                <TableHead className="text-center">Statut</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {supplierProducts.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={8} className="text-center py-12 text-gray-400">
                                                        <Package className="h-10 w-10 mx-auto mb-2 opacity-30" />
                                                        Aucun produit Fournisseur trouvé.
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                supplierProducts.map(p => (
                                                    <TableRow key={p.id}>
                                                        <TableCell>
                                                            <div className="w-10 h-10 rounded bg-gray-100 overflow-hidden border">
                                                                {p.images && p.images[0]
                                                                    ? <img src={p.images[0]} alt="" className="w-full h-full object-cover" />
                                                                    : <Package className="w-full h-full p-2 text-gray-300" />}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="font-medium line-clamp-1 max-w-[160px]">{p.title}</div>
                                                            <div className="text-xs text-gray-400 font-mono">{p.id.slice(0, 8)}...</div>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Badge variant="outline" className="text-blue-700 border-blue-200 bg-blue-50">{p.store_name}</Badge>
                                                        </TableCell>
                                                        <TableCell className="text-right font-bold">{p.price.toLocaleString()}</TableCell>
                                                        <TableCell className="text-right font-mono">{p.stock_quantity}</TableCell>
                                                        <TableCell className="text-center">
                                                            {p.min_wholesale_quantity
                                                                ? <Badge className="bg-blue-100 text-blue-800 border-blue-200 text-sm px-3">{p.min_wholesale_quantity} unités</Badge>
                                                                : <Badge variant="outline" className="text-gray-400">Non défini</Badge>}
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="flex items-center gap-2">
                                                                <Input
                                                                    type="number"
                                                                    min="1"
                                                                    className="h-8 w-24 text-center font-mono"
                                                                    value={editingMoq[p.id] ?? ""}
                                                                    onChange={e => setEditingMoq(prev => ({ ...prev, [p.id]: e.target.value }))}
                                                                    placeholder={p.min_wholesale_quantity ?? "–"}
                                                                />
                                                                <Button
                                                                    size="sm"
                                                                    className="h-8 bg-blue-600 hover:bg-blue-700"
                                                                    onClick={() => handleSaveMoq(p.id)}
                                                                    disabled={savingMoq[p.id] || String(editingMoq[p.id]) === String(p.min_wholesale_quantity)}
                                                                >
                                                                    {savingMoq[p.id] ? "..." : <><Edit2 className="h-3 w-3 mr-1" />Sauv.</>}
                                                                </Button>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="text-center">
                                                            <Badge className={p.status === 'ACTIVE' ? 'bg-green-500' : 'bg-gray-400'}>
                                                                {p.status === 'ACTIVE' ? 'Actif' : p.status}
                                                            </Badge>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                    {/* ALERTS TAB */}
                    <TabsContent value="alerts">
                        <Card>
                            <CardHeader><CardTitle>Alertes SLA & Retards Livraison</CardTitle></CardHeader>
                            <CardContent className="p-0">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Type</TableHead>
                                            <TableHead>Détails</TableHead>
                                            <TableHead>Sévérité</TableHead>
                                            <TableHead>Date</TableHead>
                                            <TableHead>Statut</TableHead>
                                            <TableHead className="text-right">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {alerts.map(a => (
                                            <TableRow key={a.id}>
                                                <TableCell><Badge variant="outline">{a.alert_type}</Badge></TableCell>
                                                <TableCell>
                                                    <div className="text-sm font-medium">{a.description}</div>
                                                    <div className="text-xs text-gray-500">Commande: {a.order_id}</div>
                                                </TableCell>
                                                <TableCell>
                                                    <Badge className={a.severity === 'HIGH' ? 'bg-red-500' : 'bg-yellow-500'}>{a.severity}</Badge>
                                                </TableCell>
                                                <TableCell>{new Date(a.triggered_at).toLocaleString()}</TableCell>
                                                <TableCell>{a.is_resolved ? <Badge className="bg-green-500">Résolue</Badge> : <Badge className="bg-orange-500">Ouverte</Badge>}</TableCell>
                                                <TableCell className="text-right">
                                                    {!a.is_resolved && (
                                                        <Button size="sm" onClick={() => handleResolveAlert(a.id)}>Archiver</Button>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {alerts.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8">Aucune alerte active.</TableCell></TableRow>}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* DISPUTES TAB */}
                    <TabsContent value="disputes">
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <AlertTriangle className="h-5 w-5 text-red-600" />
                                    Gestion des Litiges
                                    {disputes.filter(d => !['RESOLVED', 'REJECTED'].includes(d.status)).length > 0 && (
                                        <Badge className="bg-red-500 text-white ml-2">
                                            {disputes.filter(d => !['RESOLVED', 'REJECTED'].includes(d.status)).length} actifs
                                        </Badge>
                                    )}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-0">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>N° Cmd</TableHead>
                                            <TableHead>Client / Vendeur</TableHead>
                                            <TableHead>Motif</TableHead>
                                            <TableHead>Vendeur a répondu</TableHead>
                                            <TableHead>Montant</TableHead>
                                            <TableHead>Statut</TableHead>
                                            <TableHead className="text-right">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {disputes.map(d => (
                                            <TableRow key={d.id} className={d.status === 'OPEN' ? 'bg-red-50/40' : d.status === 'SELLER_RESPONDED' ? 'bg-blue-50/40' : ''}>
                                                <TableCell className="font-mono text-sm">{d.order_number}</TableCell>
                                                <TableCell>
                                                    <div className="text-sm font-semibold">{d.buyer_name}</div>
                                                    <div className="text-xs text-gray-500">→ {d.store_name}</div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="font-medium text-red-700 text-sm">{d.reason}</div>
                                                    {d.description && <div className="text-xs text-gray-500 truncate max-w-[160px]">{d.description}</div>}
                                                </TableCell>
                                                <TableCell>
                                                    {d.seller_response ? (
                                                        <Badge className="bg-blue-100 text-blue-800 border-blue-200">Répondu ✅</Badge>
                                                    ) : (
                                                        <Badge className="bg-gray-100 text-gray-500">En attente...</Badge>
                                                    )}
                                                </TableCell>
                                                <TableCell className="font-bold">{(d.total_amount || 0).toFixed(2)} HTG</TableCell>
                                                <TableCell>
                                                    {(() => {
                                                        const sc = {
                                                            OPEN: 'bg-red-500',
                                                            SELLER_RESPONDED: 'bg-blue-500',
                                                            INVESTIGATION: 'bg-purple-500',
                                                            RESOLVED: 'bg-green-500',
                                                            REJECTED: 'bg-gray-400',
                                                        };
                                                        return <Badge className={sc[d.status] || 'bg-gray-400'}>{d.status}</Badge>;
                                                    })()}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {!['RESOLVED', 'REJECTED'].includes(d.status) && (
                                                        <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={() => {
                                                            setSelectedDispute(d);
                                                            setVerdictType('REFUND_CLIENT');
                                                            setVerdictNote('');
                                                            setVerdictPartialBuyer('');
                                                            setVerdictPartialSeller('');
                                                            setDisputeDialogOpen(true);
                                                        }}>Arbitrer</Button>
                                                    )}
                                                    {d.status === 'RESOLVED' && (
                                                        <span className="text-xs text-gray-500">⚖️ {d.admin_verdict}</span>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {disputes.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8">Aucun litige en cours.</TableCell></TableRow>}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* FINANCIALS TAB */}
                    <TabsContent value="financials">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <Card>
                                <CardHeader><CardTitle>Soldes Portefeuilles Vendeurs</CardTitle></CardHeader>
                                <CardContent className="p-0">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Nom</TableHead>
                                                <TableHead className="text-right">Solde</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {walletStats.map(w => (
                                                <TableRow key={w.user_id}>
                                                    <TableCell>{w.name}</TableCell>
                                                    <TableCell className="text-right font-bold text-green-600">{w.wallet_balance.toLocaleString()} HTG</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader><CardTitle>Audit des Transactions</CardTitle></CardHeader>
                                <CardContent className="p-0">
                                    <div className="max-h-[500px] overflow-auto">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Date</TableHead>
                                                    <TableHead>Type</TableHead>
                                                    <TableHead className="text-right">Montant</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {transactions.map(t => (
                                                    <TableRow key={t.id}>
                                                        <TableCell className="text-xs">{new Date(t.created_at).toLocaleDateString()}</TableCell>
                                                        <TableCell>
                                                            <div className="text-xs font-medium">{t.transaction_type}</div>
                                                            <div className="text-[10px] text-gray-500">{t.reason}</div>
                                                        </TableCell>
                                                        <TableCell className={`text-right font-bold text-xs ${t.amount < 0 ? 'text-red-500' : 'text-green-600'}`}>
                                                            {t.amount > 0 ? '+' : ''}{t.amount.toLocaleString()} HTG
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>

                    {/* ── Frais livraison Sure More ─────────────────────────────── */}
                    <TabsContent value="payables">
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <DollarSign className="h-5 w-5 text-emerald-600" />
                                    Frais de livraison dus à Sure More
                                </CardTitle>
                                <p className="text-sm text-gray-500">
                                    Montants que la plateforme doit verser à Sure More pour les livraisons complétées via leur réseau.
                                </p>
                            </CardHeader>
                            <CardContent className="space-y-4">

                                {/* Filtres statuts */}
                                <div className="flex gap-2 flex-wrap">
                                    {[
                                        { key: 'INVOICE_REQUESTED', label: '📄 Factures reçues', color: 'bg-blue-100 text-blue-700 border-blue-200' },
                                        { key: 'PENDING',           label: '⏳ En attente',      color: 'bg-amber-100 text-amber-700 border-amber-200' },
                                        { key: 'PAID',              label: '✓ Payés',             color: 'bg-green-100 text-green-700 border-green-200' },
                                    ].map(s => (
                                        <button key={s.key}
                                            onClick={() => { setPayableFilter(s.key); fetchPayables(s.key); }}
                                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${
                                                payableFilter === s.key ? s.color : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
                                            }`}>
                                            {s.label}
                                        </button>
                                    ))}
                                </div>

                                {/* Résumé montants */}
                                {payables.length > 0 && (
                                    <div className="flex gap-4 p-3 bg-gray-50 rounded-xl text-sm">
                                        <div>
                                            <span className="text-gray-400">Total filtré :</span>
                                            <span className="font-bold ml-1">
                                                {payables.reduce((s, p) => s + p.amount, 0).toLocaleString('fr')} HTG
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-gray-400">{payables.length} transaction(s)</span>
                                        </div>
                                    </div>
                                )}

                                {/* Table */}
                                {payablesLoading ? (
                                    <div className="flex justify-center py-10">
                                        <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                                    </div>
                                ) : payables.length === 0 ? (
                                    <div className="text-center py-10 text-gray-400">
                                        <p className="text-3xl mb-2">💸</p>
                                        <p className="text-sm font-medium">Aucune transaction dans cette catégorie</p>
                                    </div>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Commande</TableHead>
                                                <TableHead>Mission</TableHead>
                                                <TableHead>Montant</TableHead>
                                                <TableHead>Statut</TableHead>
                                                <TableHead>Date facture</TableHead>
                                                <TableHead>Référence</TableHead>
                                                <TableHead></TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {payables.map(p => (
                                                <TableRow key={p.id}>
                                                    <TableCell className="font-mono text-xs">
                                                        #{p.order_number || p.order_id?.substring(0,8)}
                                                    </TableCell>
                                                    <TableCell className="font-mono text-xs text-gray-400">
                                                        {p.mission_id?.substring(0,12) || '—'}
                                                    </TableCell>
                                                    <TableCell className="font-bold">
                                                        {p.amount.toLocaleString('fr')} HTG
                                                    </TableCell>
                                                    <TableCell>
                                                        {p.status === 'INVOICE_REQUESTED' && (
                                                            <Badge className="bg-blue-100 text-blue-700 border-blue-200">📄 Facture reçue</Badge>
                                                        )}
                                                        {p.status === 'PENDING' && (
                                                            <Badge className="bg-amber-100 text-amber-700 border-amber-200">⏳ En attente</Badge>
                                                        )}
                                                        {p.status === 'PAID' && (
                                                            <Badge className="bg-green-100 text-green-700">✓ Payé</Badge>
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="text-xs text-gray-500">
                                                        {p.invoice_requested_at
                                                            ? new Date(p.invoice_requested_at).toLocaleDateString('fr-FR')
                                                            : '—'}
                                                    </TableCell>
                                                    <TableCell className="text-xs text-gray-400">
                                                        {p.payment_reference || '—'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {['INVOICE_REQUESTED', 'PENDING'].includes(p.status) && (
                                                            <Button size="sm"
                                                                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-7"
                                                                onClick={() => {
                                                                    setPayableDialog(p);
                                                                    setPaymentRef('');
                                                                    setPaymentNote('');
                                                                }}>
                                                                💳 Payer
                                                            </Button>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                </Tabs>

                {/* ── Dialog confirmation paiement Sure More ────────────────── */}
                <Dialog open={!!payableDialog} onOpenChange={open => !open && setPayableDialog(null)}>
                    <DialogContent className="max-w-md">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <DollarSign className="h-5 w-5 text-emerald-600" />
                                Confirmer le paiement Sure More
                            </DialogTitle>
                        </DialogHeader>
                        {payableDialog && (
                            <div className="space-y-4 py-2">
                                {/* Résumé */}
                                <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-500">Commande</span>
                                        <span className="font-mono font-semibold">#{payableDialog.order_number}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-500">Montant à virer</span>
                                        <span className="font-bold text-emerald-700 text-lg">
                                            {payableDialog.amount.toLocaleString('fr')} HTG
                                        </span>
                                    </div>
                                    {payableDialog.invoice_note && (
                                        <p className="text-xs text-gray-500 italic border-t pt-2">
                                            Note : {payableDialog.invoice_note}
                                        </p>
                                    )}
                                </div>

                                {/* Référence paiement */}
                                <div>
                                    <Label className="text-sm font-semibold">
                                        Référence de paiement <span className="text-red-500">*</span>
                                    </Label>
                                    <Input className="mt-1.5"
                                        placeholder="N° transaction MonCash / virement bancaire"
                                        value={paymentRef}
                                        onChange={e => setPaymentRef(e.target.value)}
                                    />
                                    <p className="text-xs text-gray-400 mt-1">
                                        Entrez le numéro de transaction après avoir effectué le virement.
                                    </p>
                                </div>

                                <div>
                                    <Label className="text-sm font-semibold">Note interne (optionnel)</Label>
                                    <Textarea className="mt-1.5" rows={2}
                                        placeholder="Ex: Virement MonCash #09271234 — lot avril 2026"
                                        value={paymentNote}
                                        onChange={e => setPaymentNote(e.target.value)}
                                    />
                                </div>

                                <div className="flex gap-3 pt-2">
                                    <Button variant="outline" className="flex-1"
                                        onClick={() => setPayableDialog(null)}>
                                        Annuler
                                    </Button>
                                    <Button
                                        className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                                        disabled={!paymentRef.trim() || payingId === payableDialog.id}
                                        onClick={() => handlePayPayable(payableDialog.id)}>
                                        {payingId === payableDialog.id ? 'Traitement...' : '✓ Confirmer le paiement'}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </DialogContent>
                </Dialog>

                {/* Existing dialogs below... */}

                {/* Dispute Resolution Dialog */}
                <Dialog open={disputeDialogOpen} onOpenChange={setDisputeDialogOpen}>
                    <DialogContent className="max-w-2xl">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <AlertTriangle className="h-5 w-5 text-purple-600" />
                                Arbitrage du Litige — {selectedDispute?.order_number}
                            </DialogTitle>
                        </DialogHeader>
                        {selectedDispute && (
                            <div className="space-y-4 py-2 max-h-[70vh] overflow-y-auto pr-1">
                                {/* Overview */}
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div className="bg-gray-50 rounded p-2">
                                        <p className="text-xs text-gray-400 mb-0.5">Acheteur</p>
                                        <p className="font-semibold">{selectedDispute.buyer_name}</p>
                                        <p className="text-xs text-gray-500">{selectedDispute.buyer_email}</p>
                                    </div>
                                    <div className="bg-gray-50 rounded p-2">
                                        <p className="text-xs text-gray-400 mb-0.5">Vendeur / Boutique</p>
                                        <p className="font-semibold">{selectedDispute.seller_name}</p>
                                        <p className="text-xs text-gray-500">{selectedDispute.store_name}</p>
                                    </div>
                                </div>

                                {/* Buyer's complaint */}
                                <div className="bg-red-50 rounded-lg p-3 border border-red-100">
                                    <p className="text-xs font-bold text-red-500 uppercase mb-1">Réclamation acheteur</p>
                                    <p className="font-semibold text-sm">{selectedDispute.reason}</p>
                                    {selectedDispute.description && <p className="text-sm text-red-800 mt-1">{selectedDispute.description}</p>}
                                    {selectedDispute.evidence_images?.length > 0 && (
                                        <div className="flex flex-wrap gap-2 mt-2">
                                            {selectedDispute.evidence_images.map((url, i) => (
                                                <a key={i} href={url} target="_blank" rel="noreferrer" title={`Preuve ${i+1}`}>
                                                    {url.match(/\.(jpg|jpeg|png|webp)/i)
                                                        ? <img src={url} alt={`Preuve ${i+1}`} className="w-20 h-20 object-cover rounded border hover:opacity-90 transition" />
                                                        : <span className="text-xs text-blue-600 underline">📎 Preuve {i+1}</span>
                                                    }
                                                </a>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Seller's response */}
                                {selectedDispute.seller_response ? (
                                    <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                                        <p className="text-xs font-bold text-blue-500 uppercase mb-1">Réponse vendeur</p>
                                        <p className="text-sm text-blue-900">{selectedDispute.seller_response}</p>
                                        {selectedDispute.seller_evidence_images?.length > 0 && (
                                            <div className="flex flex-wrap gap-2 mt-2">
                                                {selectedDispute.seller_evidence_images.map((url, i) => (
                                                    <a key={i} href={url} target="_blank" rel="noreferrer" title={`Preuve vendeur ${i+1}`}>
                                                        {url.match(/\.(jpg|jpeg|png|webp)/i)
                                                            ? <img src={url} alt={`Preuve vendeur ${i+1}`} className="w-20 h-20 object-cover rounded border hover:opacity-90 transition" />
                                                            : <span className="text-xs text-blue-600 underline">📎 Preuve {i+1}</span>
                                                        }
                                                    </a>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-3 text-sm text-yellow-700">
                                        ⚠️ Le vendeur n'a pas encore répondu. Vous pouvez arbitrer maintenant ou attendre sa réponse.
                                    </div>
                                )}

                                {/* Verdict */}
                                <div className="space-y-1.5">
                                    <Label className="font-semibold">Verdict <span className="text-red-500">*</span></Label>
                                    <select
                                        className="w-full border rounded-md px-3 py-2 text-sm"
                                        value={verdictType}
                                        onChange={e => setVerdictType(e.target.value)}
                                    >
                                        <option value="REFUND_CLIENT">Rembourser intégralement l'acheteur ({(selectedDispute.total_amount || 0).toFixed(2)} HTG)</option>
                                        <option value="RELEASE_SELLER">Donner raison au vendeur (libérer les fonds)</option>
                                        <option value="PARTIAL">Remboursement partiel (partager)</option>
                                    </select>
                                </div>

                                {/* Partial amounts */}
                                {verdictType === 'PARTIAL' && (
                                    <div className="grid grid-cols-2 gap-4 bg-orange-50 border border-orange-100 rounded-lg p-3">
                                        <div className="space-y-1">
                                            <Label className="text-sm">Montant acheteur (HTG)</Label>
                                            <Input
                                                type="number" min="0" step="0.01"
                                                placeholder="0.00"
                                                value={verdictPartialBuyer}
                                                onChange={e => setVerdictPartialBuyer(e.target.value)}
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-sm">Montant vendeur (HTG)</Label>
                                            <Input
                                                type="number" min="0" step="0.01"
                                                placeholder="0.00"
                                                value={verdictPartialSeller}
                                                onChange={e => setVerdictPartialSeller(e.target.value)}
                                            />
                                        </div>
                                        <p className="col-span-2 text-xs text-gray-500">
                                            Total commande : {(selectedDispute.total_amount || 0).toFixed(2)} HTG
                                        </p>
                                    </div>
                                )}

                                <div className="space-y-1.5">
                                    <Label className="font-semibold">Note explicative (visible par toutes les parties)</Label>
                                    <Textarea
                                        placeholder="Expliquez votre décision en détail. Cette note sera visible par l'acheteur et le vendeur."
                                        value={verdictNote}
                                        onChange={e => setVerdictNote(e.target.value)}
                                        rows={4}
                                    />
                                </div>
                            </div>
                        )}
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setDisputeDialogOpen(false)}>Annuler</Button>
                            <Button
                                className="bg-purple-600 hover:bg-purple-700"
                                onClick={handleResolveDispute}
                                disabled={disputeResolveLoading}
                            >
                                {disputeResolveLoading ? 'Traitement...' : '⚖️ Valider le Verdict'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Store Approval Dialog */}
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>
                                {actionType === 'approve' ? "Approuver la boutique" : "Rejeter la boutique"}
                            </DialogTitle>
                        </DialogHeader>
                        <div className="py-4">
                            <p className="mb-4">
                                Vous êtes sur le point de {actionType === 'approve' ? "valider" : "rejeter"} <strong>{selectedStore?.name}</strong>.
                            </p>
                            {actionType === 'reject' && (
                                <div className="space-y-2">
                                    <Label>Motif du rejet (Sera envoyé au client)</Label>
                                    <Textarea
                                        value={rejectReason}
                                        onChange={(e) => setRejectReason(e.target.value)}
                                        placeholder="Ex: Documents illisibles, activité non autorisée..."
                                    />
                                </div>
                            )}
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
                            <Button
                                className={actionType === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
                                onClick={handleStoreAction}
                            >
                                Confirmer
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </div>
    );
}

function StatCard({ title, value, icon: Icon, color }) {
    return (
        <Card>
            <CardContent className="p-6 flex items-center justify-between">
                <div>
                    <p className="text-sm text-gray-500 font-medium">{title}</p>
                    <p className="text-2xl font-bold">{value}</p>
                </div>
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-white ${color}`}>
                    <Icon className="h-6 w-6" />
                </div>
            </CardContent>
        </Card>
    );
}
